import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Form, 
  Input, 
  Button, 
  Card, 
  Space, 
  InputNumber, 
  message, 
  Spin,
  Select,
  Upload,
  Tabs,
  Divider,
  Switch,
  Table,
  Popconfirm,
  Modal,
  Tag,
  Tooltip,
  Row,
  Col,
  Steps
} from 'antd';
import { 
  ArrowLeftOutlined, 
  SaveOutlined,
  UploadOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import { supabase } from '../../lib/supabase';
import { formatPrice, formatNumber, parseNumber } from '../../utils/format';

const { TextArea } = Input;
const { Title } = Typography;
const { TabPane } = Tabs;

interface ProductFormData {
  naam: string;
  beschrijving: string;
  prijs: number;
  leverancier_id: string | null;
  artikelnummer?: string; // Optional as it's auto-generated
  artikel_per: 'stuk' | 'kg' | '1000 stuks';
  materiaalsoort: string;
  status_nieuw_recycled: string;  // Changed from status_optie
  status_eenmalig_hergebruik: string;  // New field
  status_recycled_verbranding_stort: string;  // New field
  verpakking_type: string;  // New field for packaging type
  kwaliteit: string;
  lengte_mm: number | null;
  breedte_mm: number | null;
  hoogte_mm: number | null;
  gewicht_per_stuk: number | null;
  levertijd_dagen: number | null;
  moq: number | null;
  palletisering: string;
  leveranciers_referentie?: string;  // Added supplier reference field
  transportmiddel?: 'vrachtwagen' | 'luchtvracht' | 'zeevracht'; // Transport type
  footprint?: number; // CO2 footprint value
  percentage_recycled?: number; // Percentage of recycled material
  percentage_inkleuring?: number; // Percentage of coloring
}

interface MaterialData {
  id: string;
  materiaal_naam: string;
  tarief_2025: number;
  gewicht_kg: number;
  verpact_bijdrage: number;
}

interface MaterialFormData {
  materiaal_id: string;
  gewicht_kg: number;
}

interface AdminProductFormProps {
  viewOnly?: boolean;
}

const initialFormData: ProductFormData = {
  naam: '',
  beschrijving: '',
  prijs: 0,
  leverancier_id: null,
  artikel_per: 'stuk',
  materiaalsoort: '',
  status_nieuw_recycled: '',  // Changed field
  status_eenmalig_hergebruik: '',  // New field
  status_recycled_verbranding_stort: '',  // New field
  verpakking_type: '',
  kwaliteit: '',
  lengte_mm: null,
  breedte_mm: null,
  hoogte_mm: null,
  gewicht_per_stuk: null,
  levertijd_dagen: null,
  moq: null,
  palletisering: '',
  leveranciers_referentie: '',  // Added supplier reference field
  transportmiddel: undefined, // Transport type
  footprint: undefined, // CO2 footprint value
  percentage_recycled: undefined, // Percentage of recycled material
  percentage_inkleuring: undefined, // Percentage of coloring
};

export default function AdminProductForm({ viewOnly = false }: AdminProductFormProps) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [materialForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [leveranciers, setLeveranciers] = useState<{ id: string; naam: string }[]>([]);
  const [activeTab, setActiveTab] = useState('1');
  const [productImages, setProductImages] = useState<any[]>([]);
  const [productCertificates, setProductCertificates] = useState<any[]>([]);
  const [productMaterials, setProductMaterials] = useState<MaterialData[]>([]);
  const [materialen, setMaterialen] = useState<any[]>([]);
  const [materialModalVisible, setMaterialModalVisible] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<MaterialData | null>(null);
  const [totalVerpactBijdrage, setTotalVerpactBijdrage] = useState<number>(0);
  const [formChanged, setFormChanged] = useState(false);
  const isNewProduct = !id;
  const [isPerKg, setIsPerKg] = useState<boolean>(false);
  const [tempMaterials, setTempMaterials] = useState<MaterialData[]>([]);

  // Load saved draft when component mounts
  useEffect(() => {
    const draftKey = isNewProduct ? 'newProductDraft' : `productDraft_${id}`;
    const savedDraft = localStorage.getItem(draftKey);
    
    if (savedDraft) {
      const draft = JSON.parse(savedDraft);
      form.setFieldsValue(draft);
    }
  }, [id, isNewProduct, form]);

  // Save draft when form values change
  const handleFormChange = useCallback(() => {
    const values = form.getFieldsValue();
    const draftKey = isNewProduct ? 'newProductDraft' : `productDraft_${id}`;
    localStorage.setItem(draftKey, JSON.stringify(values));
    setFormChanged(true);
  }, [form, id, isNewProduct]);

  // Clear draft after successful save
  const clearDraft = useCallback(() => {
    const draftKey = isNewProduct ? 'newProductDraft' : `productDraft_${id}`;
    localStorage.removeItem(draftKey);
    setFormChanged(false);
  }, [id, isNewProduct]);

  // Warn user before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (formChanged) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formChanged]);

  // Handle navigation away from form
  const handleNavigateAway = (path: string) => {
    if (formChanged) {
      Modal.confirm({
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Do you want to leave this page? Your draft will be saved automatically.',
        okText: 'Leave',
        cancelText: 'Stay',
        onOk: () => navigate(path)
      });
    } else {
      navigate(path);
    }
  };

  // Fetch suppliers and product data when component mounts
  useEffect(() => {
    fetchLeveranciers();
    fetchMaterialen();
    if (!isNewProduct) {
      fetchProduct(id as string);
      fetchProductImages();
      fetchProductCertificates();
      fetchProductMaterials();
    }
  }, [id]);

  // Calculate total Verpact contribution when materials change
  useEffect(() => {
    if (productMaterials.length > 0) {
      const total = productMaterials.reduce((sum, material) => sum + Number(material.verpact_bijdrage), 0);
      setTotalVerpactBijdrage(total);
    } else {
      setTotalVerpactBijdrage(0);
    }
  }, [productMaterials]);

  // Add a useEffect to handle the Unit Type change
  useEffect(() => {
    // Watch for changes in the artikel_per field
    const unitType = form.getFieldValue('artikel_per');
    const isKg = unitType === 'kg';
    setIsPerKg(isKg);
    
    // If unit type is "per kilogram", set the weight to 1
    if (isKg) {
      form.setFieldValue('gewicht_per_stuk', 1);
    }
  }, [id]); // Only run this on initial load

  // Add a form field watcher
  const handleFieldsChange = () => {
    const unitType = form.getFieldValue('artikel_per');
    const isKg = unitType === 'kg';
    
    // Update state to re-render the component and disable the field if needed
    if (isKg !== isPerKg) {
      setIsPerKg(isKg);
      
      // If changed to kg, set weight to 1
      if (isKg) {
        form.setFieldValue('gewicht_per_stuk', 1);
      }
    }
  };

  async function fetchLeveranciers() {
    try {
      const { data, error } = await supabase
        .from('leveranciers')
        .select('id, naam')
        .order('naam');

      if (error) throw error;
      setLeveranciers(data || []);
    } catch (error: any) {
      console.error('Error fetching suppliers:', error);
      message.error('Error loading suppliers: ' + error.message);
    }
  }

  async function fetchMaterialen() {
    try {
      const { data, error } = await supabase
        .from('materiaal')
        .select('*')
        .order('naam');

      if (error) throw error;
      setMaterialen(data || []);
    } catch (error: any) {
      console.error('Error fetching materials:', error);
      message.error('Error loading materials: ' + error.message);
    }
  }

  const fetchProduct = async (productId: string) => {
    setLoading(true);
    try {
      console.log('Fetching product with ID:', productId);
      
      // Simple select first to check if the product exists
      const { data, error } = await supabase
        .from('producten')
        .select('*')
        .eq('id', productId)
        .single();
      
      if (error) {
        console.error('Error fetching product:', error);
        throw error;
      }
      
      if (!data) {
        console.error('No product found with ID:', productId);
        throw new Error('Product not found');
      }
      
      console.log('Product fetched successfully:', data.naam);
      form.setFieldsValue(data);
      
    } catch (error: any) {
      console.error('Error in fetchProduct:', error);
      message.error('Error loading product: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  async function fetchProductImages() {
    try {
      const { data, error } = await supabase
        .from('product_afbeeldingen')
        .select('*')
        .eq('product_id', id)
        .order('is_primary', { ascending: false });

      if (error) throw error;
      setProductImages(data || []);
    } catch (error: any) {
      console.error('Error fetching product images:', error);
      message.error('Error loading product images: ' + error.message);
    }
  }

  async function fetchProductCertificates() {
    try {
      const { data, error } = await supabase
        .from('product_certificeringen')
        .select('*')
        .eq('product_id', id);

      if (error) throw error;
      setProductCertificates(data || []);
    } catch (error: any) {
      console.error('Error fetching product certificates:', error);
      message.error('Error loading product certificates: ' + error.message);
    }
  }

  async function fetchProductMaterials() {
    try {
      const { data, error } = await supabase.rpc('get_product_materialen', { product_id: id });

      if (error) throw error;
      setProductMaterials(data || []);
    } catch (error: any) {
      console.error('Error fetching product materials:', error);
      message.error('Error loading product materials: ' + error.message);
    }
  }

  const handleSubmit = async (values: ProductFormData) => {
    if (viewOnly) return;
    
    try {
      setLoading(true);

      // For new products, don't send artikelnummer to allow the trigger to generate it
      if (isNewProduct && !values.artikelnummer) {
        delete values.artikelnummer;
      }

      // Check if user is an admin first
      const { data: session } = await supabase.auth.getSession();
      const userId = session?.session?.user?.id;
      
      if (!userId) {
        throw new Error("You must be logged in to perform this action");
      }

      // Ensure we're using the correct column names (mm instead of cm)
      const formattedValues = {
        ...values,
        lengte_mm: values.lengte_mm,
        breedte_mm: values.breedte_mm,
        hoogte_mm: values.hoogte_mm
      };

      if (isNewProduct) {
        // First check if the user has admin access
        const { data: isAdmin, error: adminCheckError } = await supabase.rpc('is_admin');
        
        if (adminCheckError) {
          console.error("Error checking admin status:", adminCheckError);
        }
        
        // Insert the product with the correct column names
        const { data, error } = await supabase
          .from('producten')
          .insert([formattedValues])
          .select();

        if (error) {
          if (!isAdmin) {
            throw error;
          } else {
            throw error;
          }
        }
        
        if (data && data.length > 0) {
          // Add the materials if any were added
          if (tempMaterials.length > 0) {
            const materialsToAdd = tempMaterials.map(material => ({
              product_id: data[0].id,
              materiaal_id: material.id,
              gewicht_kg: material.gewicht_kg
            }));

            const { error: materialsError } = await supabase
              .from('product_materialen')
              .insert(materialsToAdd);

            if (materialsError) {
              console.error("Error adding materials:", materialsError);
              message.warning('Product created but there was an error adding materials');
            }
          }

          // Ensure the user has access to the product they just created
          try {
            const insertAccess = await supabase
              .from('product_access')
              .insert({
                product_id: data[0].id,
                user_id: userId,
                is_creator: true
              });
            
            if (insertAccess.error) {
              console.error("Error setting product access:", insertAccess.error);
            }
          } catch (accessError) {
            console.error("Failed to set product access:", accessError);
          }
          
          clearDraft();
          message.success('Product created successfully!');
          setTimeout(() => {
            navigate(`/admin/products/${data[0].id}`);
          }, 1500);
        }
      } else {
        // When updating, first check if user has admin or creator access
        const { data: hasAccess, error: accessCheckError } = await supabase
          .from('product_access')
          .select('is_creator')
          .eq('product_id', id)
          .eq('user_id', userId)
          .maybeSingle();
        
        const isCreator = hasAccess?.is_creator || false;
        
        // Also check if user is admin
        const { data: isAdmin, error: adminCheckError } = await supabase.rpc('is_admin');
        
        if (!isCreator && !isAdmin) {
          throw new Error("You don't have permission to edit this product");
        }
        
        const { error } = await supabase
          .from('producten')
          .update(formattedValues)
          .eq('id', id);

        if (error) throw error;
        
        clearDraft(); // Clear draft after successful save
        message.success('Product updated successfully!');
        // Don't navigate away, stay on the form 
        // Refresh product data to ensure we have the latest version
        fetchProduct(id as string);
      }
    } catch (error: any) {
      console.error('Error saving product:', error);
      message.error('Error saving product: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const uploadFile = async (file: File, type: 'image' | 'certificate') => {
    if (!id || viewOnly) return;

    try {
      setUploading(true);
      
      // Create a unique file path
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `${type === 'image' ? 'product-images' : 'product-certificates'}/${id}/${fileName}`;
      
      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('products')
        .upload(filePath, file);
        
      if (uploadError) throw uploadError;
      
      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('products')
        .getPublicUrl(filePath);
        
      // Save to database
      const tableName = type === 'image' ? 'product_afbeeldingen' : 'product_certificeringen';
      const isPrimary = type === 'image' ? 
        !(await supabase.from('product_afbeeldingen').select('*').eq('product_id', id)).data?.length : 
        undefined;
        
      const { error: dbError } = await supabase
        .from(tableName)
        .insert({
          product_id: id,
          file_name: file.name,
          file_path: filePath,
          file_type: file.type,
          ...(type === 'image' && { is_primary: isPrimary })
        });
        
      if (dbError) throw dbError;
      
      message.success(`${type === 'image' ? 'Image' : 'Certificate'} uploaded successfully`);
      
      // Refresh the data
      if (type === 'image') fetchProductImages();
      else fetchProductCertificates();
      
    } catch (error: any) {
      console.error(`Error uploading ${type}:`, error);
      message.error(`Error uploading ${type}: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleImageUpload = async (options: any) => {
    if (viewOnly) return;
    const { file } = options;
    await uploadFile(file, 'image');
  };

  const handleCertificateUpload = async (options: any) => {
    if (viewOnly) return;
    const { file } = options;
    await uploadFile(file, 'certificate');
  };

  const setPrimaryImage = async (imageId: string) => {
    if (viewOnly) return;
    try {
      // First, set all images to not primary
      await supabase
        .from('product_afbeeldingen')
        .update({ is_primary: false })
        .eq('product_id', id);
        
      // Then set the selected image as primary
      await supabase
        .from('product_afbeeldingen')
        .update({ is_primary: true })
        .eq('id', imageId);
        
      message.success('Primary image updated');
      fetchProductImages();
    } catch (error: any) {
      console.error('Error setting primary image:', error);
      message.error('Error setting primary image: ' + error.message);
    }
  };

  const deleteFile = async (fileId: string, type: 'image' | 'certificate') => {
    if (viewOnly) return;
    try {
      const tableName = type === 'image' ? 'product_afbeeldingen' : 'product_certificeringen';
      
      // Get the file path first
      const { data, error: fetchError } = await supabase
        .from(tableName)
        .select('file_path')
        .eq('id', fileId)
        .single();
        
      if (fetchError) throw fetchError;
      
      // Delete from storage
      if (data?.file_path) {
        const { error: storageError } = await supabase.storage
          .from('products')
          .remove([data.file_path]);
          
        if (storageError) throw storageError;
      }
      
      // Delete from database
      const { error: deleteError } = await supabase
        .from(tableName)
        .delete()
        .eq('id', fileId);
        
      if (deleteError) throw deleteError;
      
      message.success(`${type === 'image' ? 'Image' : 'Certificate'} deleted successfully`);
      
      // Refresh the data
      if (type === 'image') fetchProductImages();
      else fetchProductCertificates();
      
    } catch (error: any) {
      console.error(`Error deleting ${type}:`, error);
      message.error(`Error deleting ${type}: ${error.message}`);
    }
  };

  const showAddMaterialModal = () => {
    materialForm.resetFields();
    setEditingMaterial(null);
    setMaterialModalVisible(true);
  };

  const showEditMaterialModal = (record: MaterialData) => {
    setEditingMaterial(record);
    materialForm.setFieldsValue({
      materiaal_id: record.id,
      gewicht_kg: record.gewicht_kg
    });
    setMaterialModalVisible(true);
  };

  const handleMaterialModalCancel = () => {
    setMaterialModalVisible(false);
    materialForm.resetFields();
  };

  const handleAddMaterial = async (values: MaterialFormData) => {
    try {
      // Get the product's total weight to calculate material weight
      const productWeight = form.getFieldValue('gewicht_per_stuk') || 0;
      const unitType = form.getFieldValue('artikel_per') || 'stuk';
      
      if (productWeight <= 0) {
        message.warning('Please enter a Weight Per Unit for the product first');
        return;
      }
      
      // Convert weight to kg if needed based on unit type
      let weightInKg = productWeight;
      if (unitType === '1000 stuks') {
        weightInKg = productWeight / 1000;
      }

      if (isNewProduct) {
        // For new products, store materials temporarily
        const material = materialen.find(m => m.id === values.materiaal_id);
        if (material) {
          const newMaterial = {
            id: material.id,
            materiaal_naam: material.naam,
            tarief_2025: material.tarief_2025,
            gewicht_kg: weightInKg,
            verpact_bijdrage: material.tarief_2025 * weightInKg
          };

          if (editingMaterial) {
            setTempMaterials(prev => prev.map(m => 
              m.id === editingMaterial.id ? newMaterial : m
            ));
          } else {
            setTempMaterials(prev => [...prev, newMaterial]);
          }
          
          message.success(`Material ${editingMaterial ? 'updated' : 'added'} successfully`);
          setMaterialModalVisible(false);
          return;
        }
      }

      if (!id) return;

      // If editing, delete the existing relationship first
      if (editingMaterial) {
        await supabase
          .from('product_materialen')
          .delete()
          .eq('product_id', id)
          .eq('materiaal_id', editingMaterial.id);
      }

      // Insert the new relationship - use the entire product weight
      const { error } = await supabase
        .from('product_materialen')
        .insert({
          product_id: id,
          materiaal_id: values.materiaal_id,
          gewicht_kg: weightInKg
        });

      if (error) throw error;

      message.success(`Material ${editingMaterial ? 'updated' : 'added'} successfully`);
      setMaterialModalVisible(false);
      fetchProductMaterials();
    } catch (error: any) {
      console.error('Error adding material:', error);
      message.error('Error adding material: ' + error.message);
    }
  };

  const handleDeleteMaterial = async (materialId: string) => {
    try {
      if (isNewProduct) {
        setTempMaterials(prev => prev.filter(m => m.id !== materialId));
        message.success('Material removed successfully');
        return;
      }

      if (!id) return;

      const { error } = await supabase
        .from('product_materialen')
        .delete()
        .eq('product_id', id)
        .eq('materiaal_id', materialId);

      if (error) throw error;

      message.success('Material removed successfully');
      fetchProductMaterials();
    } catch (error: any) {
      console.error('Error removing material:', error);
      message.error('Error removing material: ' + error.message);
    }
  };

  const handleDeleteProduct = async () => {
    if (viewOnly || !id) return;
    
    Modal.confirm({
      title: 'Delete Product',
      content: 'Are you sure you want to delete this product? This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setLoading(true);
          
          // First delete all related records (materials, images, certificates)
          await supabase
            .from('product_materialen')
            .delete()
            .eq('product_id', id);
            
          await supabase
            .from('product_afbeeldingen')
            .delete()
            .eq('product_id', id);
            
          await supabase
            .from('product_certificeringen')
            .delete()
            .eq('product_id', id);
            
          await supabase
            .from('product_access')
            .delete()
            .eq('product_id', id);
            
          // Finally delete the product itself
          const { error } = await supabase
            .from('producten')
            .delete()
            .eq('id', id);
            
          if (error) throw error;
          
          message.success('Product deleted successfully');
          navigate('/admin/products');
        } catch (error: any) {
          console.error('Error deleting product:', error);
          message.error('Error deleting product: ' + error.message);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  if (loading && !isNewProduct) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="admin-product-form" style={{ maxWidth: 1200, margin: '0 auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: 24,
          padding: '16px 0'
        }}>
          <Space>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => handleNavigateAway('/admin/products')}
            >
              Terug naar Producten
            </Button>
            <Title level={4} style={{ margin: 0 }}>
              {isNewProduct ? 'Nieuw Product Toevoegen' : viewOnly ? 'Product Bekijken' : 'Product Bewerken'}
            </Title>
          </Space>
          
          {!isNewProduct && !viewOnly && (
            <Popconfirm
              title="Weet je zeker dat je dit product wilt verwijderen?"
              description="Deze actie kan niet ongedaan worden gemaakt."
              onConfirm={handleDeleteProduct}
              okText="Ja, Verwijderen"
              cancelText="Nee"
              okButtonProps={{ danger: true }}
            >
              <Button 
                danger
                icon={<DeleteOutlined />}
              >
                Product Verwijderen
              </Button>
            </Popconfirm>
          )}
        </div>

        {isNewProduct && (
          <div style={{ marginBottom: 24, background: '#f5f5f5', padding: 16, borderRadius: 8 }}>
            <Steps size="small" current={activeTab === '1' ? 0 : 1}>
              <Steps.Step title="Basisinformatie" description="Vul de productgegevens in" />
              <Steps.Step title="Materialen" description="Voeg materialen toe" />
            </Steps>
          </div>
        )}

        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          type="card"
          className="product-tabs"
          style={{ marginBottom: 24 }}
        >
          <TabPane 
            tab={isNewProduct ? (
              <span>
                <Tag style={{ marginRight: 8, backgroundColor: activeTab === '1' ? '#1890ff' : '#f0f0f0', color: activeTab === '1' ? 'white' : 'rgba(0,0,0,0.65)' }}>
                  1
                </Tag>
                Basisinformatie
              </span>
            ) : "Basisinformatie"}
            key="1"
          >
            <Card bordered={false} style={{ borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={initialFormData}
                disabled={viewOnly}
                onFieldsChange={handleFormChange}
                style={{ maxWidth: 1000, margin: '0 auto' }}
              >
                <Row gutter={24}>
                  <Col span={24}>
                    {!isNewProduct && (
                      <Form.Item
                        name="artikelnummer"
                        label="Artikelnummer"
                        tooltip="Dit veld wordt automatisch gegenereerd voor nieuwe producten"
                      >
                        <Input disabled={true} />
                      </Form.Item>
                    )}

                    <Form.Item
                      name="naam"
                      label="Productnaam"
                      rules={[{ required: true, message: 'Voer de productnaam in!' }]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      name="beschrijving"
                      label="Beschrijving"
                    >
                      <TextArea rows={4} />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="prijs"
                      label="Prijs"
                      rules={[{ required: true, message: 'Voer de prijs in!' }]}
                    >
                      <InputNumber
                        prefix="€"
                        style={{ width: '100%' }}
                        precision={2}
                        min={0}
                        step={0.01}
                        controls={false}
                        placeholder="0,00"
                        decimalSeparator=","
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="leverancier_id"
                      label="Leverancier"
                    >
                      <Select
                        placeholder="Selecteer een leverancier"
                        allowClear
                        options={leveranciers.map(lev => ({ value: lev.id, label: lev.naam }))}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="leveranciers_referentie"
                      label="Leverancier Referentie"
                      tooltip="De referentiecode die de leverancier gebruikt voor dit product"
                    >
                      <Input placeholder="Referentiecode van de leverancier" />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="artikel_per"
                      label={
                        <span>
                          Verkoopeenheid 
                          <Tooltip title="Hoe het product wordt verkocht - per stuk, per kg of per 1000 stuks">
                            <InfoCircleOutlined style={{ marginLeft: 8 }} />
                          </Tooltip>
                        </span>
                      }
                    >
                      <Select 
                        options={[
                          { value: 'stuk', label: 'Per Stuk' },
                          { value: 'kg', label: 'Per Kilogram' },
                          { value: '1000 stuks', label: 'Per 1000 Stuks' },
                        ]} 
                        onChange={(value) => {
                          // If unit type is "per kilogram", set the weight to 1 and disable the field
                          if (value === 'kg') {
                            form.setFieldValue('gewicht_per_stuk', 1);
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="gewicht_per_stuk"
                      label={
                        <span>
                          Productgewicht (kg)
                          <Tooltip title="Gewicht per enkel producteenheid in kilogram. Voor 'Per 1000 Stuks', voer het gewicht van alle 1000 stuks in. Voor 'Per Kilogram' wordt dit automatisch ingesteld op 1 kg.">
                            <InfoCircleOutlined style={{ marginLeft: 8 }} />
                          </Tooltip>
                        </span>
                      }
                      rules={[{ required: true, message: 'Voer het productgewicht in!' }]}
                    >
                      <InputNumber 
                        min={0} 
                        style={{ width: '100%' }} 
                        precision={2}
                        step={0.01}
                        controls={false}
                        placeholder="0,00"
                        decimalSeparator=","
                        disabled={isPerKg}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Divider orientation="left" style={{ marginTop: 8, marginBottom: 24 }}>Materiaal & Kwaliteit</Divider>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="materiaalsoort"
                      label={
                        <span>
                          Materiaalsoort
                          <Tooltip title="Het primaire materiaal (bijv. karton, plastic, glas)">
                            <InfoCircleOutlined style={{ marginLeft: 8 }} />
                          </Tooltip>
                        </span>
                      }
                    >
                      <Input placeholder="bijv. Karton, Plastic, Glas" />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="status_nieuw_recycled"
                      label={
                        <span>
                          Status Nieuw/Recycled
                          <Tooltip title="Geef aan of het product nieuw of recycled is">
                            <InfoCircleOutlined style={{ marginLeft: 8 }} />
                          </Tooltip>
                        </span>
                      }
                    >
                      <Select
                        placeholder="Selecteer optie"
                        allowClear
                        options={[
                          { value: 'nieuw', label: 'Nieuw' },
                          { value: 'recycled', label: 'Recycled' },
                        ]}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="percentage_recycled"
                      label={
                        <span>
                          Percentage Recycled
                          <Tooltip title="Percentage gerecycled materiaal in het product">
                            <InfoCircleOutlined style={{ marginLeft: 8 }} />
                          </Tooltip>
                        </span>
                      }
                    >
                      <InputNumber
                        min={0}
                        max={100}
                        formatter={(value) => `${value}%`}
                        parser={(value) => value ? parseFloat(value.replace('%', '')) : 0}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="percentage_inkleuring"
                      label={
                        <span>
                          Percentage Inkleuring
                          <Tooltip title="Percentage inkleuring in het product">
                            <InfoCircleOutlined style={{ marginLeft: 8 }} />
                          </Tooltip>
                        </span>
                      }
                    >
                      <InputNumber
                        min={0}
                        max={100}
                        formatter={(value) => `${value}%`}
                        parser={(value) => value ? parseFloat(value.replace('%', '')) : 0}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="status_eenmalig_hergebruik"
                      label={
                        <span>
                          Status Eenmalig/Hergebruik
                          <Tooltip title="Geef aan of het product eenmalig is of hergebruikt kan worden">
                            <InfoCircleOutlined style={{ marginLeft: 8 }} />
                          </Tooltip>
                        </span>
                      }
                    >
                      <Select
                        placeholder="Selecteer optie"
                        allowClear
                        options={[
                          { value: 'eenmalig', label: 'Eenmalig' },
                          { value: 'hergebruik', label: 'Hergebruik' },
                        ]}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="status_recycled_verbranding_stort"
                      label={
                        <span>
                          Status End-of-Life
                          <Tooltip title="Geef aan wat er gebeurt aan het einde van de levensduur">
                            <InfoCircleOutlined style={{ marginLeft: 8 }} />
                          </Tooltip>
                        </span>
                      }
                    >
                      <Select
                        placeholder="Selecteer optie"
                        allowClear
                        options={[
                          { value: 'recycled', label: 'Recycled' },
                          { value: 'verbranding', label: 'Verbranding' },
                          { value: 'stort', label: 'Stort' },
                        ]}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="verpakking_type"
                      label={
                        <span>
                          Verpakkingstype
                          <Tooltip title="Het type verpakking dat dit product vertegenwoordigt">
                            <InfoCircleOutlined style={{ marginLeft: 8 }} />
                          </Tooltip>
                        </span>
                      }
                    >
                      <Select
                        placeholder="Selecteer verpakkingstype"
                        allowClear
                        options={[
                          { value: 'primaire-verpakking', label: 'Primaire verpakking' },
                          { value: 'secundaire-verpakking', label: 'Secundaire verpakking' },
                          { value: 'tertiaire-verpakking', label: 'Tertiaire verpakking' },
                          { value: 'verkooppunt-verpakking', label: 'Verkooppunt verpakking' },
                          { value: 'sup-verpakking', label: 'SUP verpakking' },
                        ]}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="transportmiddel"
                      label={
                        <span>
                          Transportmiddel
                          <Tooltip title="Type van transport voor dit product">
                            <InfoCircleOutlined style={{ marginLeft: 8 }} />
                          </Tooltip>
                        </span>
                      }
                    >
                      <Select
                        placeholder="Selecteer transportmiddel"
                        allowClear
                        options={[
                          { value: 'vrachtwagen', label: 'Vrachtwagen' },
                          { value: 'luchtvracht', label: 'Luchtvracht' },
                          { value: 'zeevracht', label: 'Zeevracht' },
                        ]}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="footprint"
                      label={
                        <span>
                          CO₂ Footprint
                          <Tooltip title="CO₂ footprint waarde">
                            <InfoCircleOutlined style={{ marginLeft: 8 }} />
                          </Tooltip>
                        </span>
                      }
                    >
                      <InputNumber
                        min={0}
                        precision={2}
                        addonAfter="CO₂"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="kwaliteit"
                      label="Kwaliteit"
                    >
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Divider orientation="left" style={{ marginTop: 8, marginBottom: 24 }}>Afmetingen</Divider>
                  </Col>
                  
                  <Col span={8}>
                    <Form.Item
                      name="lengte_mm"
                      label="Lengte (mm)"
                    >
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      name="breedte_mm"
                      label="Breedte (mm)"
                    >
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      name="hoogte_mm"
                      label="Hoogte (mm)"
                    >
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Divider orientation="left" style={{ marginTop: 8, marginBottom: 24 }}>Logistiek & Bedrukking</Divider>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      name="levertijd_dagen"
                      label="Levertijd (dagen)"
                    >
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      name="moq"
                      label="Minimale Bestelhoeveelheid"
                    >
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      name="palletisering"
                      label="Palletisering"
                    >
                      <Input />
                    </Form.Item>
                  </Col>

                  {!viewOnly && (
                    <Col span={24} style={{ textAlign: 'right', marginTop: 16 }}>
                      <Form.Item style={{ marginBottom: 0 }}>
                        <Button
                          type="primary"
                          onClick={() => {
                            // Validate the current form fields before proceeding
                            form.validateFields()
                              .then(() => {
                                setActiveTab('2'); // Switch to Materialen tab
                              })
                              .catch((error) => {
                                console.log('Validation failed:', error);
                              });
                          }}
                          icon={<ArrowRightOutlined />}
                          size="large"
                        >
                          Volgende
                        </Button>
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              </Form>
            </Card>
          </TabPane>

          <TabPane 
            tab={isNewProduct ? (
              <span>
                <Tag style={{ marginRight: 8, backgroundColor: activeTab === '2' ? '#1890ff' : '#f0f0f0', color: activeTab === '2' ? 'white' : 'rgba(0,0,0,0.65)' }}>
                  2
                </Tag>
                Materialen
              </span>
            ) : "Materialen"}
            key="2"
          >
            <Card bordered={false} style={{ borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: 24,
                  padding: '0 0 16px 0',
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <Typography.Title level={5} style={{ margin: 0 }}>Productmaterialen</Typography.Title>
                  {!viewOnly && (
                    <Space>
                      <Button
                        icon={<PlusOutlined />}
                        onClick={showAddMaterialModal}
                      >
                        Materiaal Toevoegen
                      </Button>
                    </Space>
                  )}
                </div>

                {(isNewProduct ? tempMaterials : productMaterials).length > 0 ? (
                  <>
                    <Table
                      dataSource={isNewProduct ? tempMaterials : productMaterials}
                      rowKey="id"
                      pagination={false}
                      bordered
                      style={{ marginBottom: 16 }}
                    >
                      <Table.Column
                        title="Materiaal"
                        dataIndex="materiaal_naam"
                        key="materiaal_naam"
                      />
                      <Table.Column
                        title="Gewicht (kg)"
                        dataIndex="gewicht_kg"
                        key="gewicht_kg"
                        render={(text) => formatNumber(Number(text), 2)}
                      />
                      <Table.Column
                        title="Tarief 2025 (€/kg)"
                        dataIndex="tarief_2025"
                        key="tarief_2025"
                        render={(text) => formatPrice(Number(text))}
                      />
                      <Table.Column
                        title="Verpact Bijdrage (€)"
                        dataIndex="verpact_bijdrage"
                        key="verpact_bijdrage"
                        render={(text) => formatPrice(Number(text))}
                      />
                      {!viewOnly && (
                        <Table.Column
                          title="Acties"
                          key="actions"
                          render={(_, record: MaterialData) => (
                            <Space>
                              <Button
                                icon={<EditOutlined />}
                                size="small"
                                onClick={() => showEditMaterialModal(record)}
                              />
                              <Popconfirm
                                title="Weet je zeker dat je dit materiaal wilt verwijderen?"
                                onConfirm={() => handleDeleteMaterial(record.id)}
                                okText="Ja"
                                cancelText="Nee"
                              >
                                <Button
                                  icon={<DeleteOutlined />}
                                  size="small"
                                  danger
                                />
                              </Popconfirm>
                            </Space>
                          )}
                        />
                      )}
                    </Table>
                    
                    <div style={{ 
                      marginTop: 16, 
                      textAlign: 'right', 
                      padding: '16px 8px',
                      background: '#f9f9f9',
                      borderRadius: 8
                    }}>
                      <Typography.Text strong>
                        Totale Verpact Bijdrage: <Tag color="blue" style={{ fontSize: 16, padding: '4px 8px' }}>
                          {formatPrice(isNewProduct ? 
                            tempMaterials.reduce((sum, m) => sum + (m.verpact_bijdrage || 0), 0) : 
                            totalVerpactBijdrage
                          )}
                        </Tag>
                      </Typography.Text>
                    </div>
                  </>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '40px 20px',
                    background: '#f9f9f9',
                    borderRadius: 8
                  }}>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 16 }}>
                      Nog geen materialen toegevoegd aan dit product.
                    </Typography.Text>
                  </div>
                )}

                <div style={{ textAlign: 'right', marginTop: 24 }}>
                  <Button
                    type="primary"
                    onClick={() => form.submit()}
                    icon={<SaveOutlined />}
                    size="large"
                  >
                    Product Aanmaken
                  </Button>
                </div>
              </div>
            </Card>
          </TabPane>

          {!isNewProduct && (
            <TabPane tab="Afbeeldingen" key="3">
              <Card bordered={false} style={{ borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: 24,
                  padding: '0 0 16px 0',
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <Typography.Title level={5} style={{ margin: 0 }}>Productafbeeldingen</Typography.Title>
                  {!viewOnly && (
                    <Upload
                      customRequest={handleImageUpload}
                      fileList={[]}
                      showUploadList={false}
                      accept="image/*"
                    >
                      <Button 
                        icon={<UploadOutlined />} 
                        loading={uploading}
                      >
                        Afbeelding Uploaden
                      </Button>
                    </Upload>
                  )}
                </div>
                
                <div style={{ marginTop: 16 }}>
                  {productImages.length === 0 ? (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '40px 20px',
                      background: '#f9f9f9',
                      borderRadius: 8
                    }}>
                      <Typography.Text type="secondary" style={{ fontSize: 16 }}>
                        Nog geen afbeeldingen geüpload.
                      </Typography.Text>
                    </div>
                  ) : (
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: 24
                    }}>
                      {productImages.map(image => (
                        <div 
                          key={image.id} 
                          style={{ 
                            position: 'relative', 
                            border: image.is_primary ? '2px solid #1890ff' : '1px solid #f0f0f0',
                            borderRadius: 8,
                            padding: 12,
                            width: 220,
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                            transition: 'all 0.3s ease',
                            backgroundColor: image.is_primary ? '#e6f7ff' : 'white'
                          }}
                        >
                          <img 
                            src={supabase.storage.from('products').getPublicUrl(image.file_path).data.publicUrl} 
                            alt={image.file_name} 
                            style={{ 
                              width: '100%', 
                              height: 180, 
                              objectFit: 'contain', 
                              marginBottom: 12,
                              borderRadius: 4
                            }} 
                          />
                          <div>
                            <div style={{ 
                              fontSize: 13, 
                              color: '#666', 
                              textOverflow: 'ellipsis', 
                              overflow: 'hidden', 
                              whiteSpace: 'nowrap',
                              marginBottom: 12
                            }}>
                              {image.file_name}
                            </div>
                            {!viewOnly && (
                              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between' }}>
                                <Button 
                                  size="small" 
                                  type={image.is_primary ? "primary" : "default"}
                                  onClick={() => setPrimaryImage(image.id)}
                                  disabled={image.is_primary}
                                >
                                  {image.is_primary ? 'Primary' : 'Set Primary'}
                                </Button>
                                <Button 
                                  size="small" 
                                  danger 
                                  onClick={() => deleteFile(image.id, 'image')}
                                >
                                  Delete
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            </TabPane>
          )}

          {!isNewProduct && (
            <TabPane tab="Certificates" key="4">
              <Card bordered={false} style={{ borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: 24,
                  padding: '0 0 16px 0',
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <Typography.Title level={5} style={{ margin: 0 }}>Product Certificates</Typography.Title>
                  {!viewOnly && (
                    <Upload
                      customRequest={handleCertificateUpload}
                      fileList={[]}
                      showUploadList={false}
                      accept=".pdf,.doc,.docx,.xls,.xlsx"
                    >
                      <Button 
                        icon={<UploadOutlined />} 
                        loading={uploading}
                      >
                        Upload Certificate
                      </Button>
                    </Upload>
                  )}
                </div>
                
                <div style={{ marginTop: 16 }}>
                  {productCertificates.length === 0 ? (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '40px 20px',
                      background: '#f9f9f9',
                      borderRadius: 8
                    }}>
                      <Typography.Text type="secondary" style={{ fontSize: 16 }}>
                        No certificates uploaded yet.
                      </Typography.Text>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {productCertificates.map(cert => (
                        <Card 
                          key={cert.id} 
                          size="small" 
                          title={cert.file_name}
                          style={{
                            borderRadius: 8,
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                          }}
                          extra={
                            !viewOnly && (
                              <Button 
                                size="small" 
                                danger 
                                onClick={() => deleteFile(cert.id, 'certificate')}
                              >
                                Delete
                              </Button>
                            )
                          }
                        >
                          <a 
                            href={supabase.storage.from('products').getPublicUrl(cert.file_path).data.publicUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" stroke="#1890ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M14 2V8H20" stroke="#1890ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M12 18V12" stroke="#1890ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M9 15L12 18L15 15" stroke="#1890ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            View Certificate
                          </a>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            </TabPane>
          )}
        </Tabs>
      </Space>

      {/* Material Add/Edit Modal */}
      <Modal
        title={editingMaterial ? "Materiaal Bewerken" : "Materiaal Toevoegen"}
        open={materialModalVisible}
        onCancel={handleMaterialModalCancel}
        footer={null}
        width={500}
        bodyStyle={{ padding: '24px' }}
      >
        <Form
          form={materialForm}
          layout="vertical"
          onFinish={handleAddMaterial}
        >
          <Form.Item
            name="materiaal_id"
            label="Materiaal"
            rules={[{ required: true, message: 'Selecteer een materiaal!' }]}
          >
            <Select
              placeholder="Selecteer een materiaal"
              disabled={!!editingMaterial}
              options={materialen.map(material => ({
                value: material.id,
                label: `${material.naam} (${formatPrice(material.tarief_2025)}/kg)`
              }))}
              optionFilterProp="label"
              showSearch
              style={{ width: '100%' }}
            />
          </Form.Item>

          <div style={{ 
            padding: '12px 16px', 
            background: '#e6f7ff', 
            borderRadius: 8, 
            marginBottom: 24,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8
          }}>
            <InfoCircleOutlined style={{ marginTop: 3, color: '#1890ff' }} /> 
            <div>
              <p style={{ margin: 0 }}>
                Het materiaal zal het volledige productgewicht ({form.getFieldValue('gewicht_per_stuk')} kg) gebruiken zoals ingevoerd in het tabblad basisinformatie.
              </p>
            </div>
          </div>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleMaterialModalCancel}>
                Annuleren
              </Button>
              <Button type="primary" htmlType="submit">
                {editingMaterial ? 'Bijwerken' : 'Toevoegen'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
} 